import React, { useEffect, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import Modal from 'shared/ui/Modal/Modal'
import { noteTakerStore } from 'shared/stores/noteTakerStore'
import { studentsStore } from 'pages/Students/model/studentsStore'
import { ensureStudentsLoaded } from 'pages/Students/model/ensureStudentsLoaded'
import { buildStudentNameIndex } from 'shared/utils/buildStudentNameIndex'
import NoteTakerDashboardWidget from './NoteTakerDashboardWidget'
import AssignRecordingStudentModal from './AssignRecordingStudentModal'
import { useNoteTakerTranscript } from './hooks/useNoteTakerTranscript'
import { useNoteTakerRecordingLifecycle } from './hooks/useNoteTakerRecordingLifecycle'
import { useNoteTakerAiProcessing } from './hooks/useNoteTakerAiProcessing'
import s from './NoteTakerShell.module.scss'

const NoteTakerShell = observer(function NoteTakerShell() {
	const shouldShowNoteWidget = noteTakerStore.shouldShowWidget
	const isNoteRecording = noteTakerStore.isRecording
	const noteStatus = noteTakerStore.status
	const transcriptPreview = noteTakerStore.transcriptPreview
	const students = studentsStore.students

	useNoteTakerTranscript()
	useNoteTakerRecordingLifecycle()
	useNoteTakerAiProcessing()

	const studentNameIndex = useMemo(
		() => buildStudentNameIndex(students),
		[students],
	)

	useEffect(() => {
		ensureStudentsLoaded().catch(error => {
			console.error('Failed to prepare student list for note taker:', error)
		})
	}, [])

	useEffect(() => {
		if (!isNoteRecording) return undefined
		const intervalId = window.setInterval(() => {
			noteTakerStore.tickRecording()
		}, 1000)
		return () => window.clearInterval(intervalId)
	}, [isNoteRecording])

	useEffect(() => {
		if (noteTakerStore.studentSelectionLocked || noteTakerStore.detectedStudentId) {
			return
		}
		if (noteStatus === 'recording') {
			noteTakerStore.syncDetectedStudentFromTranscript(studentNameIndex)
			return
		}
		if (noteStatus === 'paused') {
			noteTakerStore.syncDetectedStudentFromTranscript(studentNameIndex, {
				forceFuzzy: true,
			})
		}
	}, [noteStatus, transcriptPreview, studentNameIndex])

	return (
		<>
			{shouldShowNoteWidget ? (
				<div className={s.noteWidgetRail} data-note-widget-root="true">
					<NoteTakerDashboardWidget />
				</div>
			) : null}

			<AssignRecordingStudentModal />

			<Modal
				isOpen={noteTakerStore.isCancelConfirmOpen}
				onClose={() => noteTakerStore.closeCancelRecording()}
				title="Cancel recording?"
				subtitle="This will discard the current transcript and note draft."
				ariaLabel="Cancel recording confirmation"
				width={520}
				footerButtons={[
					{
						id: 'keep',
						text: 'Keep recording',
						variant: 'secondary',
						onClick: () => noteTakerStore.closeCancelRecording(),
					},
					{
						id: 'discard',
						text: 'Discard recording',
						variant: 'primary',
						onClick: () => noteTakerStore.confirmCancelRecording(),
					},
				]}
			>
				<div className={s.cancelCopy}>
					You can resume instead if you want to keep this transcript moving.
				</div>
			</Modal>
		</>
	)
})

export default NoteTakerShell
